import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useRemovePersonFromOrganizationMutation } from "@/features/people/graphql/generated";
import { ModalFileName } from "@/shared/modals";
import { useModal } from "@/shared/utils/hooks/useModal";
import { ConfirmModalProps } from "@/shared/widgets/Confirm";
import { useOrganisation } from "./useOrganisation";
import { useToast } from "@/shared/contexts/toast.context";

interface RemovePersonFromOrganizationParams {
  personId: number;
  orgId?: number;
}

interface RemovePersonFromOrganizationWithNameParams
  extends RemovePersonFromOrganizationParams {
  name: string;
}

export const useRemovePersonFromOrganization = () => {
  const [showConfirm] = useModal(ModalFileName.CONFIRM);
  const { organization } = useOrganisation();
  const selectedOrgId = organization?.id;
  const { t } = useTranslation();
  const [removePerson, { data, loading, error }] =
    useRemovePersonFromOrganizationMutation({
      refetchQueries: ["People", "Organization"],
    });
  const client = useApolloClient();

  const { showToast } = useToast();

  const handleRemovePersonFromOrganization = async (
    params: RemovePersonFromOrganizationParams
  ) => {
    const response = await removePerson({
      variables: {
        orgId: selectedOrgId,
        ...params,
      },
    });

    if (!response.data?.removePersonFromOrganization?.success) {
      showToast({
        message: "Error!",
        description:
          response.data?.removePersonFromOrganization?.message ||
          "Something went wrong",
        type: "error",
      });
    }
    if (response.data?.removePersonFromOrganization?.success) {
      showToast({
        message: "Success!",
        description:
          response.data?.removePersonFromOrganization?.message ||
          "Person removed from organization",
        type: "success",
      });
    }
  };

  const handleRemovePersonWithConfirm = (
    params: RemovePersonFromOrganizationWithNameParams,
    modalParams?: ConfirmModalProps
  ) => {
    showConfirm({
      title: t("organisation.confirm_remove_member", { name: params.name }),
      description: t("organisation.confirm_remove_member_description", {
        name: params.name,
      }),
      disclaimer: t("common.remove_action_discaimer"),
      confirmLabel: t("common.confirm_remove"),
      cancelLabel: t("common.cancel_remove"),
      confirmButtonVariant: "destructive",
      onConfirm: () => handleRemovePersonFromOrganization(params),
      ...modalParams,
    });
  };

  return {
    loading,
    removePersonFromOrganizationWithConfirm: handleRemovePersonWithConfirm,
    removePersonFromOrganization: handleRemovePersonFromOrganization,
  };
};
